/**
 * Marca de CaliPeak — punto unico de personalizacion visual (RF-DEV-03).
 *
 * Cambiar un valor de aqui repinta toda la aplicacion: los colores se
 * inyectan como variables CSS en :root al arrancar (ver applyBranding).
 * No hace falta tocar Tailwind ni ningun componente.
 */

export interface BrandColors {
  crimson: string;
  crimsonDark: string;
  gold: string;
  goldLight: string;
  bronze: string;
  obsidian: string;
  stone: string;
  marble: string;
  success: string;
  warning: string;
  error: string;
}

export interface Branding {
  name: string;
  tagline: { es: string; en: string };
  domain: string;
  siteUrl: string;
  socialHandle: string;
  contactEmail: string;
  colors: BrandColors;
  /** Color de la barra del navegador y del manifiesto PWA. */
  themeColor: string;
  /** Tema por defecto de la interfaz (RNF-09). */
  defaultTheme: 'dark' | 'light';
  /** Sonidos opcionales del apartado 3.4; desactivados por defecto. */
  soundsEnabledByDefault: boolean;
}

/** Paleta de la seccion 3.2 de la especificacion funcional. */
export const branding: Branding = {
  name: 'CaliPeak',
  tagline: {
    es: 'La cima se gana cada quincena',
    en: 'The peak is earned every fortnight',
  },
  domain: 'calipeak.app',
  siteUrl: import.meta.env?.VITE_SITE_URL ?? 'https://calipeak.app',
  socialHandle: '@calipeak',
  contactEmail: 'hola@calipeak.app',
  colors: {
    crimson: '#B3121B',
    crimsonDark: '#7A0A11',
    gold: '#D4AF37',
    goldLight: '#F2D479',
    bronze: '#8C6239',
    obsidian: '#12100E',
    stone: '#1E1B18',
    marble: '#F5F1E8',
    success: '#2E7D4F',
    warning: '#C87A1E',
    error: '#C0392B',
  },
  themeColor: '#B3121B',
  defaultTheme: 'dark',
  // Sin tipografias: cada mundo visual declara la suya en index.css.
  soundsEnabledByDefault: false,
};

/** `#B3121B` -> `179 18 27`, el formato que espera Tailwind con `<alpha-value>`. */
export function hexToRgbChannels(hex: string): string {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const int = Number.parseInt(full, 16);
  if (Number.isNaN(int) || full.length !== 6) return '0 0 0';
  return `${(int >> 16) & 255} ${(int >> 8) & 255} ${int & 255}`;
}

/**
 * Vuelca la paleta en las variables CSS de :root. Se llama una vez al
 * arrancar (src/main.tsx) para que un cambio en `branding.colors` se refleje
 * sin recompilar Tailwind.
 *
 * Las tipografias ya NO se vuelcan aqui. Cada mundo visual declara la suya en
 * index.css (Barlow en la app y el panel, Cinzel e Inter en la portada), y un
 * valor en linea sobre <html> ganaria a cualquier regla de la hoja: la app se
 * quedaria con la letra de la portada.
 */
export function applyBranding(target: HTMLElement = document.documentElement): void {
  const map: Record<string, string> = {
    '--cp-crimson': branding.colors.crimson,
    '--cp-crimson-dark': branding.colors.crimsonDark,
    '--cp-gold': branding.colors.gold,
    '--cp-gold-light': branding.colors.goldLight,
    '--cp-bronze': branding.colors.bronze,
    '--cp-obsidian': branding.colors.obsidian,
    '--cp-stone': branding.colors.stone,
    '--cp-marble': branding.colors.marble,
    '--cp-success': branding.colors.success,
    '--cp-warning': branding.colors.warning,
    '--cp-error': branding.colors.error,
  };
  for (const [token, hex] of Object.entries(map)) {
    target.style.setProperty(token, hexToRgbChannels(hex));
  }
}
